import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiTrash2, FiPlus, FiX, FiUser, FiMail, FiLock, FiShield, FiRefreshCw, FiKey, FiSearch, FiFilter, FiChevronDown } from 'react-icons/fi';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [showRoleFilter, setShowRoleFilter] = useState(false);
  const [groupedUsers, setGroupedUsers] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({});
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [syncFilter, setSyncFilter] = useState({
    filterType: 'all',
    filterValue: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    isActive: true,
    avatar: null
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    handleSearch();
    groupUsersByRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, users, selectedRole]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/users');
      console.log('Users data received:', res.data.data);
      // Log mahasiswa with photos
      res.data.data.forEach(user => {
        if (user.role === 'mahasiswa' && user.avatar && user.avatar !== 'default-avatar.png') {
          console.log(`Mahasiswa ${user.name} (${user.nim}) has photo: ${user.avatar}`);
        }
      });
      setUsers(res.data.data);
      setFilteredUsers(res.data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Gagal mengambil data users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    let filtered = users;

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(user => {
        return (
          user.name?.toLowerCase().includes(search) ||
          user.email?.toLowerCase().includes(search) ||
          user.nim?.toLowerCase().includes(search) ||
          user.role?.toLowerCase().includes(search)
        );
      });
    }

    // Filter by role
    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }
    
    setFilteredUsers(filtered);
  };

  const groupUsersByRole = () => {
    const grouped = filteredUsers.reduce((acc, user) => {
      const role = user.role || 'unknown';
      if (!acc[role]) {
        acc[role] = [];
      }
      acc[role].push(user);
      return acc;
    }, {});
    setGroupedUsers(grouped);

    // Auto expand all roles on first load
    const expanded = {};
    Object.keys(grouped).forEach(role => {
      expanded[role] = true;
    });
    setExpandedRoles(prev => ({ ...prev, ...expanded }));
  };

  const toggleRoleExpand = (role) => {
    setExpandedRoles(prev => ({
      ...prev,
      [role]: !prev[role]
    }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus user ini?')) {
      try {
        await axios.delete(`/api/users/${id}`);
        toast.success('User berhasil dihapus');
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Gagal menghapus user');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('role', formData.role);
      submitData.append('isActive', formData.isActive);
      
      if (formData.avatar) {
        submitData.append('avatar', formData.avatar);
      }

      await axios.post('/api/users', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('User berhasil ditambahkan');
      setShowModal(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'viewer',
        isActive: true,
        avatar: null
      });
      setAvatarPreview(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menambahkan user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB');
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Format file harus JPEG, PNG, GIF, atau WebP');
        return;
      }

      setFormData(prev => ({ ...prev, avatar: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSyncMahasiswa = () => {
    // Open sync modal instead of direct sync
    setShowSyncModal(true);
  };

  const handleSyncWithFilter = async () => {
    if (!syncFilter.filterValue && syncFilter.filterType !== 'all') {
      toast.error('Masukkan nilai filter');
      return;
    }

    const filterLabel = syncFilter.filterType === 'all' 
      ? 'semua mahasiswa'
      : syncFilter.filterType === 'nim' 
      ? `NIM ${syncFilter.filterValue}`
      : `Tahun ${syncFilter.filterValue}`;

    if (!window.confirm(`Sinkronkan ${filterLabel} ke user?`)) {
      return;
    }

    setSyncing(true);
    try {
      const res = await axios.post('/api/sync-mahasiswa/filter', {
        filterType: syncFilter.filterType,
        filterValue: syncFilter.filterValue
      });
      
      toast.success(
        `Sync berhasil! Created: ${res.data.data.created}, Updated: ${res.data.data.updated}, Skipped: ${res.data.data.skipped}`
      );
      
      setShowSyncModal(false);
      setSyncFilter({ filterType: 'all', filterValue: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal sync mahasiswa');
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteAllMahasiswa = async () => {
    if (!window.confirm('⚠️ PERINGATAN!\n\nApakah Anda yakin ingin menghapus SEMUA user mahasiswa?\n\nTindakan ini tidak dapat dibatalkan!\n\nSetelah dihapus, Anda perlu sync ulang mahasiswa.')) {
      return;
    }

    // Double confirmation
    if (!window.confirm('Konfirmasi sekali lagi: Hapus SEMUA user mahasiswa?')) {
      return;
    }

    setSyncing(true);
    try {
      const res = await axios.delete('/api/users/mahasiswa/delete-all');
      toast.success(res.data.message);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menghapus user mahasiswa');
    } finally {
      setSyncing(false);
    }
  };

  const handleResetPassword = async (userId, userRole, nim) => {
    if (userRole !== 'mahasiswa') {
      toast.error('Reset password hanya untuk mahasiswa');
      return;
    }

    if (!window.confirm(`Reset password ke NIM (${nim})?`)) {
      return;
    }

    try {
      await axios.post(`/api/sync-mahasiswa/reset-password/${userId}`);
      toast.success('Password berhasil direset ke NIM');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal reset password');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      'admin': 'table-badge-danger',
      'staff': 'table-badge-success',
      'viewer': 'table-badge-info',
      'admin_pmb': 'table-badge-warning',
      'ketua': 'table-badge-purple',
      'wakil_ketua_1': 'table-badge-purple',
      'wakil_ketua_2': 'table-badge-purple',
      'wakil_ketua_3': 'table-badge-purple',
      'ketua_prodi': 'table-badge-primary',
      'sekretaris_prodi': 'table-badge-primary',
      'lpm': 'table-badge-warning',
      'lppm': 'table-badge-warning',
      'baak': 'table-badge-primary',
      'bauk': 'table-badge-primary',
      'mou': 'table-badge-info',
      'perlengkapan': 'table-badge-secondary',
      'mahasiswa': 'table-badge-cyan',
      'calon_mahasiswa': 'table-badge-pink'
    };
    return badges[role] || 'table-badge-info';
  };

  const getAvatarUrl = (avatar, userName) => {
    if (!avatar || avatar === 'default-avatar.png' || avatar === 'default-student.png') {
      // Return default avatar using UI Avatars service
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(userName || 'User')}&background=random&size=100`;
    }
    
    // Check if avatar path follows student photo structure: {program}/{tahun}/{nim}/{filename}
    // This matches the rule in students biodata where photos are organized by:
    // - Program studi (cleaned, lowercase, dash-separated)
    // - Enrollment year
    // - Student NIM
    // Example: "profesi-profesi-ners/2019/B400219172/photo-123456.jpg"
    if (avatar.includes('/')) {
      // This is a student photo path from biodata
      // Files synced from client/public/uploads/students/ to backend/uploads/
      return `http://localhost:5000/uploads/${avatar}`;
    }
    
    // For regular user avatars (non-mahasiswa or manually uploaded)
    // These are stored directly in uploads/avatars/
    return `http://localhost:5000/uploads/avatars/${avatar}`;
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'admin': 'Administrator',
      'staff': 'Staff',
      'viewer': 'Viewer',
      'admin_pmb': 'Admin PMB',
      'ketua': 'Ketua',
      'wakil_ketua_1': 'Wakil Ketua 1',
      'wakil_ketua_2': 'Wakil Ketua 2',
      'wakil_ketua_3': 'Wakil Ketua 3',
      'ketua_prodi': 'Ketua Prodi',
      'sekretaris_prodi': 'Sekretaris Prodi',
      'lpm': 'LPM',
      'lppm': 'LPPM',
      'baak': 'BAAK',
      'bauk': 'BAUK',
      'mou': 'MOU',
      'perlengkapan': 'Perlengkapan',
      'mahasiswa': 'Mahasiswa',
      'calon_mahasiswa': 'Calon Mahasiswa'
    };
    return roleNames[role] || role;
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h2>Manajemen User</h2>
          <p>Kelola user sistem</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button 
            className="btn btn-danger"
            onClick={handleDeleteAllMahasiswa}
            disabled={syncing}
            title="Hapus semua user mahasiswa"
          >
            <FiTrash2 /> Hapus Semua Mahasiswa
          </button>
          <button 
            className="btn btn-secondary"
            onClick={handleSyncMahasiswa}
            disabled={syncing}
          >
            <FiRefreshCw /> {syncing ? 'Syncing...' : 'Sync Mahasiswa'}
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <FiPlus /> Tambah User
          </button>
        </div>
      </div>

      {/* Search Box */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="search-container">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Cari berdasarkan nama, email, NIM, atau role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <FiX />
              </button>
            )}
          </div>
          
          {/* Role Filter Dropdown */}
          <div className="role-filter-container">
            <button 
              className="btn-filter"
              onClick={() => setShowRoleFilter(!showRoleFilter)}
            >
              <FiFilter />
              {selectedRole === 'all' ? 'Semua Role' : getRoleDisplayName(selectedRole)}
              <FiChevronDown className={showRoleFilter ? 'rotate' : ''} />
            </button>
            
            {showRoleFilter && (
              <div className="role-dropdown">
                <div 
                  className={`role-option ${selectedRole === 'all' ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedRole('all');
                    setShowRoleFilter(false);
                  }}
                >
                  Semua Role
                </div>
                {[
                  'super_admin', 'admin', 'mahasiswa', 'dosen', 'staff_akademik',
                  'kepala_prodi', 'dekan', 'wakil_dekan', 'rektor', 'wakil_rektor',
                  'staff_keuangan', 'staff_perpustakaan', 'staff_kemahasiswaan',
                  'staff_pmb', 'operator', 'auditor', 'alumni', 'dosen_wali', 'pembimbing_akademik'
                ].map(role => (
                  <div 
                    key={role}
                    className={`role-option ${selectedRole === role ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedRole(role);
                      setShowRoleFilter(false);
                    }}
                  >
                    {getRoleDisplayName(role)}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="search-info">
            <span>Menampilkan {filteredUsers.length} dari {users.length} user</span>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="spinner"></div>
        ) : (
          <div className="modern-table-container">
            <table className="modern-table accent-indigo">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Foto</th>
                  <th>Nama</th>
                  <th>NIM/Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedUsers).length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      {searchTerm ? 'Tidak ada user yang sesuai dengan pencarian' : 'Tidak ada data user'}
                    </td>
                  </tr>
                ) : (
                  Object.keys(groupedUsers).sort().map((role) => (
                    <React.Fragment key={role}>
                      {/* Role Group Header */}
                      <tr 
                        className="role-group-header"
                        onClick={() => toggleRoleExpand(role)}
                      >
                        <td colSpan="8">
                          <div className="role-header-content">
                            <FiChevronDown 
                              className={`chevron-icon ${expandedRoles[role] ? 'expanded' : ''}`}
                            />
                            <span className="role-title">
                              {getRoleDisplayName(role)}
                            </span>
                            <span className="role-count">
                              ({groupedUsers[role].length} user)
                            </span>
                          </div>
                        </td>
                      </tr>

                      {/* User Rows */}
                      {expandedRoles[role] && groupedUsers[role].map((user, index) => (
                        <tr key={user._id} className="role-group-row">
                          <td>{index + 1}</td>
                          <td>
                            <img 
                              src={getAvatarUrl(user.avatar, user.name)} 
                              alt={user.name}
                              className="user-avatar-table"
                              onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random&size=100`;
                              }}
                            />
                          </td>
                          <td>
                            <strong>{user.name}</strong>
                            {user.nim && (
                              <div style={{fontSize: '0.85em', color: '#666'}}>NIM: {user.nim}</div>
                            )}
                          </td>
                          <td>
                            {user.nim && <div><strong>NIM:</strong> {user.nim}</div>}
                            {user.email && <div><strong>Email:</strong> {user.email}</div>}
                          </td>
                          <td>
                            <span className={`table-badge ${getRoleBadge(user.role)}`}>
                              {getRoleDisplayName(user.role)}
                            </span>
                          </td>
                          <td>
                            <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                              {user.isActive ? 'Aktif' : 'Nonaktif'}
                            </span>
                          </td>
                          <td>
                            {user.lastLogin ? new Date(user.lastLogin).toLocaleString('id-ID') : '-'}
                          </td>
                          <td>
                            <div className="table-actions">
                              <button 
                                className="table-btn table-btn-delete" 
                                onClick={() => handleDelete(user._id)}
                                title="Delete"
                              >
                                <FiTrash2 />
                              </button>
                              {user.role === 'mahasiswa' && (
                                <button 
                                  className="table-btn table-btn-warning" 
                                  onClick={() => handleResetPassword(user._id)}
                                  title="Reset Password ke NIM"
                                >
                                  <FiKey />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sync Mahasiswa Modal */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiRefreshCw /> Sync Mahasiswa dengan Filter</h3>
              <button 
                className="modal-close"
                onClick={() => setShowSyncModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Filter Berdasarkan</label>
                <select 
                  className="form-control"
                  value={syncFilter.filterType}
                  onChange={(e) => setSyncFilter({ ...syncFilter, filterType: e.target.value, filterValue: '' })}
                >
                  <option value="all">Semua Mahasiswa</option>
                  <option value="nim">NIM Spesifik</option>
                  <option value="year">Tahun Masuk</option>
                </select>
              </div>

              {syncFilter.filterType === 'nim' && (
                <div className="form-group">
                  <label>NIM</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Contoh: B400219172"
                    value={syncFilter.filterValue}
                    onChange={(e) => setSyncFilter({ ...syncFilter, filterValue: e.target.value })}
                  />
                </div>
              )}

              {syncFilter.filterType === 'year' && (
                <div className="form-group">
                  <label>Tahun Masuk</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="Contoh: 2019"
                    min="2000"
                    max={new Date().getFullYear()}
                    value={syncFilter.filterValue}
                    onChange={(e) => setSyncFilter({ ...syncFilter, filterValue: e.target.value })}
                  />
                </div>
              )}

              {syncFilter.filterType === 'all' && (
                <div className="form-group">
                  <p style={{ color: '#6b7280', fontSize: '14px', margin: '10px 0' }}>
                    Akan melakukan sinkronisasi semua mahasiswa yang ada di database.
                  </p>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowSyncModal(false)}
                disabled={syncing}
              >
                Batal
              </button>
              <button 
                type="button"
                className="btn btn-primary"
                onClick={handleSyncWithFilter}
                disabled={syncing}
              >
                <FiRefreshCw /> {syncing ? 'Syncing...' : 'Sync Sekarang'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><FiUser /> Tambah User Baru</h3>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                <FiX />
              </button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  {/* Avatar Upload */}
                  <div className="form-group form-group-full">
                    <label>
                      <FiUser /> Foto Profil
                    </label>
                    <div className="avatar-upload-container">
                      <div className="avatar-preview">
                        <img 
                          src={avatarPreview || `https://ui-avatars.com/api/?name=${encodeURIComponent(formData.name || 'User')}&background=random&size=200`}
                          alt="Avatar Preview"
                          className="avatar-preview-img"
                        />
                      </div>
                      <div className="avatar-upload-input">
                        <input
                          type="file"
                          id="avatar"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleAvatarChange}
                          className="file-input"
                        />
                        <label htmlFor="avatar" className="file-label">
                          <FiUser /> Pilih Foto
                        </label>
                        <p className="file-hint">
                          Format: JPEG, PNG, GIF, WebP. Maks 5MB
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>
                      <FiUser /> Nama Lengkap <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Masukkan nama lengkap"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <FiMail /> Email <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="email@example.com"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <FiLock /> Password <span className="required">*</span>
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="Minimal 6 karakter"
                      minLength="6"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <FiShield /> Role <span className="required">*</span>
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="form-control"
                      required
                    >
                      <optgroup label="Sistem">
                        <option value="admin">Administrator</option>
                        <option value="staff">Staff</option>
                        <option value="viewer">Viewer</option>
                      </optgroup>
                      <optgroup label="Pimpinan">
                        <option value="ketua">Ketua</option>
                        <option value="wakil_ketua_1">Wakil Ketua 1 (Akademik)</option>
                        <option value="wakil_ketua_2">Wakil Ketua 2 (Administrasi & Keuangan)</option>
                        <option value="wakil_ketua_3">Wakil Ketua 3 (Kemahasiswaan & Kerjasama)</option>
                      </optgroup>
                      <optgroup label="Akademik">
                        <option value="ketua_prodi">Ketua Program Studi</option>
                        <option value="sekretaris_prodi">Sekretaris Program Studi</option>
                        <option value="baak">BAAK</option>
                      </optgroup>
                      <optgroup label="Administrasi">
                        <option value="bauk">BAUK</option>
                        <option value="perlengkapan">Perlengkapan</option>
                      </optgroup>
                      <optgroup label="Mutu & Penelitian">
                        <option value="lpm">LPM</option>
                        <option value="lppm">LPPM</option>
                      </optgroup>
                      <optgroup label="Kerjasama">
                        <option value="mou">MOU</option>
                      </optgroup>
                      <optgroup label="PMB">
                        <option value="admin_pmb">Admin PMB</option>
                      </optgroup>
                      <optgroup label="Pengguna">
                        <option value="mahasiswa">Mahasiswa</option>
                        <option value="calon_mahasiswa">Calon Mahasiswa</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-group form-group-full">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      <span>User Aktif</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary"
                  disabled={submitting}
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Menyimpan...' : 'Simpan User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
